import type { EntityPagerFilter } from "@/graphql/generated-types";
import pager from "@/helpers/requests/pager";
import type { IPagerState, IPagerSupport, WithId } from "@/interfaces";
import { sentryException } from "@/sentry";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import type { IdType, PageableQuery, PagerStatus } from "@/types";

type PagerListenerFunction = (selectFirstItemAutomatically: boolean) => void;

class PagerManager<T extends WithId> implements IPagerState<T> {
  public runId?: symbol;
  public items: T[];
  public status: PagerStatus;
  public listeners: Set<PagerListenerFunction>;
  public firstItemScrolled: boolean;

  constructor() {
    this.status = "notStartedYet";
    this.items = [];
    this.listeners = new Set();
    this.firstItemScrolled = false;
  }

  init() {
    this.firstItemScrolled = false;
    this.items = [];
  }

  replaceItem(id: IdType, newState?: T) {
    if (!newState) {
      return;
    }

    this.items = this.items.map((item: T) => {
      if (id && item.id === id.toString()) {
        return newState as T;
      }

      return item as T;
    });
  }

  push(item?: T) {
    if (!item) {
      return;
    }

    this.items.push(item);
  }

  deleteItem(id?: IdType) {
    this.items = this.items.filter(
      (item) => item.id?.toString() !== id?.toString(),
    );
  }

  isFirstChunkFetched() {
    return this.status === "otherChunks" || this.status === "done";
  }

  async paginate<TData>(
    query: PageableQuery<TData, EntityPagerFilter>,
    variables: EntityPagerFilter,
    store: IPagerSupport<TData>,
  ) {
    try {
      await pager(query, variables, store);
    } catch (error) {
      this.status = "done";
      sentryException(error);
      simpleError("global.error_api");
    }
  }

  addListener(callback: PagerListenerFunction) {
    if (this.status === "done") {
      return callback(true);
    }

    if (this.isFirstChunkFetched()) {
      return callback(false);
    }

    this.listeners.add(callback);
  }

  removeListener(callback: PagerListenerFunction) {
    this.listeners.delete(callback);
  }

  callListeners(selectFirstItem: boolean) {
    // Calling the subscription if there is any to select an item. But, the
    // selected item might not be fetched yet because of pagination. So we should
    // not force to select to first item. That's why we use FALSE parameter here.
    this.listeners.forEach((callback) => callback(selectFirstItem));
  }
}

export default PagerManager;
